import {
  SET_PROGRAMS_LIST,
  ADD_PROGRAM,
  UPDATE_PROGRAM,
  REMOVE_PROGRAM,
} from 'appredux/actionTypes';
import {IProductDetails} from 'constants/interfaces';

/**
 * These interfaces reflect the shape from farmInputProgramSave and your GraphQL schema:
 *
 * type FarmInputProgramInput {
 *   id: ID
 *   active: Boolean
 *   farmerId: ID
 *   produceId: ID
 *   type: String
 *   status: String
 *   hectares: Float
 *   createdDate: String
 *   updatedDate: String
 *   lines: [FarmInputProgramLineInput!]
 * }
 *
 * type FarmInputProgramLineInput {
 *   id: ID
 *   programId: ID
 *   productId: ID
 *   when: String
 *   quantity: Float
 *   PHI: Float
 *   applicationRate: Float
 *   applicationUnit: String
 *   applicationType: String
 *   applicationTypePerHectare: Float
 *   comment: String
 * }
 */

// You may want these in a shared file like 'constants/interfaces':
export interface IFarmInputProgramLineInput {
  id?: string | number;
  programId?: string | number;
  productId?: string | number;
  when?: string;
  quantity?: number;
  PHI?: number;
  applicationRate?: number;
  applicationUnit?: string;
  applicationType?: string;
  applicationTypePerHectare?: number;
  comment?: string;
  price?: number;
  product?: IProductDetails;
}

export interface IFarmInputProgramInput {
  produce?: any;
  id?: string | number;
  active?: boolean;
  farmerId?: string | number;
  produceId?: string | number;
  type?: string;
  status?: string;
  hectares?: number;
  createdDate?: string;
  updatedDate?: string;
  lines?: IFarmInputProgramLineInput[];
}

/**
 * Redux State shape: a list of programs
 */
export type ProgramsState = {
  allPrograms: IFarmInputProgramInput[];
};

/**
 * Provide some dummy data in the initialState for demonstration
 */
const initialState: ProgramsState = {
  allPrograms: [
    {
      id: 1,
      active: true,
      createdDate: '2025-02-17T10:00:00Z',
      updatedDate: '2025-02-17T12:00:00Z',
      farmerId: 101,
      produceId: 501,
      type: 'Fertilizer',
      status: 'Draft',
      hectares: 100,
      lines: [
        {
          id: 10,
          programId: 1,
          productId: 1001,
          when: 'planting',
          quantity: 10,
          PHI: 2,
          applicationRate: 1.5,
          applicationUnit: 'kg',
          applicationType: 'PerHectare',
          applicationTypePerHectare: 1,
          comment: 'First line item',
        },
      ],
    },
    {
      id: 2,
      active: false,
      createdDate: '2025-02-18T08:00:00Z',
      updatedDate: '2025-02-18T08:30:00Z',
      farmerId: 102,
      produceId: 502,
      type: 'Chemical',
      status: 'Approved',
      hectares: 50,
      lines: [
        {
          id: 20,
          programId: 2,
          productId: 2001,
          when: 'flowering',
          quantity: 5,
          PHI: 2,
          applicationRate: 2.5,
          applicationUnit: 'L',
          applicationType: 'PerHectare',
          applicationTypePerHectare: 1.5,
          comment: 'Second line item',
        },
      ],
    },
  ],
};

export default function (
  state = initialState,
  action: {type: string; payload: any},
): ProgramsState {
  switch (action.type) {
    // Replace entire list (e.g. after fetching from API)
    case SET_PROGRAMS_LIST: {
      return {
        ...state,
        allPrograms: action.payload, // expects IFarmInputProgramInput[]
      };
    }

    // Add a new program to the list
    case ADD_PROGRAM: {
      return {
        ...state,
        allPrograms: [...state.allPrograms, action.payload],
      };
    }

    // Update a program (matching by .id)
    case UPDATE_PROGRAM: {
      const updatedProgram: IFarmInputProgramInput = action.payload;
      const newList = state.allPrograms.map((prog) =>
        prog.id === updatedProgram.id ? updatedProgram : prog,
      );
      return {
        ...state,
        allPrograms: newList,
      };
    }

    // Remove a program from the list by ID
    case REMOVE_PROGRAM: {
      const programIdToRemove: string | number = action.payload;
      return {
        ...state,
        allPrograms: state.allPrograms.filter(
          (prog) => prog.id !== programIdToRemove,
        ),
      };
    }

    default:
      return state;
  }
}
